import {
  Alert,
  Modal,
  Button,
  Upload,
  Form,
  Statistic,
  Card,
  Row,
  Col,
  Result,
  Space,
} from "antd";
import { AiOutlineCloudDownload } from "react-icons/ai";
import { InboxOutlined } from "@ant-design/icons";
import { useState, useContext } from "react";
import { uploadExcel } from "@PRODUCTS";
import { exportExcel } from "@HELPERS";
import { AuthContext } from "@CONTEXTS";

const { Dragger } = Upload;

const UploadModal = ({ onClose, setStep }) => {
  const { info } = useContext(AuthContext);
  const [form] = Form.useForm();
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);
  const [errorList, setErrorList] = useState([]);
  const [error, setError] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [maxFileSizeError, setMaxFileSizeError] = useState(false);

  const isDesiOnly = info?.desiCalculationMode === "DESI_ONLY";

  const handleSave = () => {
    if (maxFileSizeError) {
      return;
    }
    form.validateFields().then(async (values) => {
      try {
        setLoading(true);
        setError(false);
        const formData = new FormData();
        formData.append("file", values.file.file);
        const result = await uploadExcel(formData);

        if (result?.success) {
          setCreatedCount(result.createdProductCount);
          setErrorList(result.errorList || []);
          setUploaded(true);
        } else {
          setError(
            "Yükleme sırasında bir sorun oluştu. Lütfen izin verilen dosya şablonuyla tekrar deneyiniz veya destek ekibiyle iletişime geçiniz."
          );
        }
      } catch (e) {
        console.log(e);
        setError(
          "Yükleme sırasında bir sorun oluştu. Lütfen izin verilen dosya şablonuyla tekrar deneyiniz veya destek ekibiyle iletişime geçiniz."
        );
      } finally {
        setLoading(false);
      }

      return false;
    });
  };

  const downloadErrorProducts = () => {
    const columns = [
      { title: "Kod", key: "Kod" },
      { title: "Ad", key: "Ad" },
      { title: "En", key: "En" },
      { title: "Boy", key: "Boy" },
      { title: "Yükseklik", key: "Yükseklik" },
      { title: "Ağırlık", key: "Ağırlık" },
    ];

    if (isDesiOnly) {
      columns.push({ title: "Desi", key: "Desi" });
    }

    columns.push({ title: "Hata Sebebi", key: "errorMessage" });

    exportExcel("Hatalı Ürünler", columns, errorList);
  };

  return (
    <Modal
      visible
      title="Excel ile Malzeme / Ürün Yükle"
      onCancel={() => {
        if (!loading) {
          setStep("TABLE");
        }
      }}
      width={900}
      footer={
        !uploaded && (
          <Space>
            <Button
              disabled={loading}
              onClick={() => {
                if (!loading) {
                  setStep("TABLE");
                }
              }}
            >
              Pencereyi Kapat
            </Button>
            <Button type="primary" loading={loading} onClick={() => handleSave()}>
              Malzeme/Ürünleri Yükle
            </Button>
          </Space>
        )
      }
    >
      {error && <Alert type="error" message={error} closable showIcon />}
      {uploaded ? (
        <>
          {errorList.length > 0 ? (
            <Row gutter={24}>
              {createdCount > 0 && (
                <Col span={12}>
                  <Card>
                    <Statistic
                      title="Başarıyla Yüklenen Malzeme/Ürün Sayısı"
                      value={createdCount}
                    />
                    <Button
                      size="small"
                      style={{ marginTop: 16 }}
                      type="primary"
                      onClick={() => setStep("TABLE")}
                    >
                      Kayıtlı Malzeme/Ürünlerim
                    </Button>
                  </Card>
                </Col>
              )}
              <Col span={createdCount === 0 ? 24 : 12}>
                <Card>
                  <Statistic
                    title="Hatalı Malzeme/Ürün Sayısı"
                    value={errorList.length}
                  />
                  <Button
                    size="small"
                    style={{ marginTop: 16 }}
                    type="primary"
                    danger
                    onClick={() => downloadErrorProducts()}
                  >
                    Hatalı Malzeme/Ürünleri İndir
                  </Button>
                </Card>
              </Col>
            </Row>
          ) : (
            <Result
              status="success"
              title={`${createdCount} malzeme/ürün başarıyla oluşturuldu.`}
              subTitle="Malzeme/Ürünleriniz başarıyla oluşturuldu."
              extra={[
                <Button
                  type="primary"
                  key="console"
                  onClick={() => setStep("TABLE")}
                >
                  Kayıtlı Malzeme/Ürünlerim
                </Button>,
                <Button
                  key="close"
                  onClick={() => {
                    onClose();
                  }}
                >
                  Kapat
                </Button>,
              ]}
            />
          )}
        </>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            message="Excel ile malzeme/ürün yükleyebilmek için örnek excel şablonunu doldurmanız gerekmektedir."
            action={
              <Button
                size="small"
                type="primary"
                icon={<AiOutlineCloudDownload />}
                onClick={() =>
                  window.open(
                    isDesiOnly
                      ? "/files/Toplu Ürün Excel.xlsx"
                      : "/files/Toplu Ürün Exceli.xlsx"
                  )
                }
              >
                Örnek Şablonu İndir
              </Button>
            }
          />
          <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
            <Form.Item
              name="file"
              label="Yüklenecek Dosya:"
              rules={[{ required: true, message: "Lütfen bir dosya seçiniz" }]}
            >
              <Dragger
                name="file"
                multiple={false}
                showUploadList={false}
                action={false}
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                beforeUpload={() => false}
                onChange={(file) => {
                  setMaxFileSizeError(false);
                  if (file.file.size > 2 * 1024 * 1024) {
                    setMaxFileSizeError(true);
                  } else {
                    setSelectedFile(file.file);
                  }
                }}
                fileList={[]}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {selectedFile
                    ? `${selectedFile.name}`
                    : "Dosya yüklemek için buraya tıklayın veya dosyanızı sürükleyip bu alana bırakın."}
                </p>
              </Dragger>
            </Form.Item>
            {maxFileSizeError && (
              <Alert
                type="error"
                showIcon
                message="Dosya boyutu izin verilen boyuttan yüksektir (2mb)."
                style={{ marginBottom: 24 }}
              />
            )}
          </Form>
        </>
      )}
    </Modal>
  );
};

export default UploadModal;
